import { resolveMediaUrl, resolveCanvasMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, Plus, Trash2, Download, ExternalLink, BarChart3, X, Loader2, ScanLine, Calendar, Copy, Check, Upload, Palette, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import FileDropzone, { IMAGE_UPLOAD } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { name: 'Classic', fg: '#000000', bg: '#FFFFFF' },
  { name: 'Ocean', fg: '#0369A1', bg: '#E0F2FE' },
  { name: 'Forest', fg: '#15803D', bg: '#F0FDF4' },
  { name: 'Sunset', fg: '#C2410C', bg: '#FFF7ED' },
  { name: 'Royal', fg: '#7C3AED', bg: '#F5F3FF' },
  { name: 'Dark', fg: '#E5E7EB', bg: '#111827' },
  { name: 'Rose', fg: '#BE185D', bg: '#FFF1F2' },
  { name: 'Gold', fg: '#A16207', bg: '#FEFCE8' },
];

const STYLES = [
  { id: 'square', label: 'Square', radius: '0' },
  { id: 'rounded', label: 'Rounded', radius: '16px' },
  { id: 'circle', label: 'Circle', radius: '50%' },
];

const QRCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [form, setForm] = useState({ url: '', title: '', fg_color: '#000000', bg_color: '#FFFFFF', logo_url: '', style: 'square' });
  const [logoUploading, setLogoUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ url: '', title: '', fg_color: '#000000', bg_color: '#FFFFFF', logo_url: '', style: 'square' });
  const [editSaving, setEditSaving] = useState(false);
  const [editLogoUploading, setEditLogoUploading] = useState(false);
  const qrRefs = useRef({});
  const logoRef = useRef(null);
  const editLogoRef = useRef(null);

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try { const res = await axios.get(`${API}/qr-codes`); setCodes(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.url.trim()) { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/qr-codes`, form);
      setCodes([res.data, ...codes]);
      setForm({ url: '', title: '', fg_color: '#000000', bg_color: '#FFFFFF', logo_url: '', style: 'square' });
      setShowForm(false);
      toast.success('QR code created!');
    } catch (e) { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/qr-codes/${id}`);
      setCodes(codes.filter(c => c.id !== id));
      if (analyticsId === id) { setAnalyticsId(null); setAnalyticsData(null); }
      toast.success('QR code deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ url: c.url, title: c.title, fg_color: c.fg_color || '#000000', bg_color: c.bg_color || '#FFFFFF', logo_url: c.logo_url || '', style: c.style || 'square' });
  };

  const cancelEdit = () => { setEditingId(null); };

  const handleUpdate = async () => {
    if (!editForm.url.trim()) { toast.error('URL is required'); return; }
    setEditSaving(true);
    try {
      const res = await axios.put(`${API}/qr-codes/${editingId}`, editForm);
      setCodes(codes.map(c => c.id === editingId ? res.data : c));
      setEditingId(null);
      toast.success('QR code updated!');
    } catch (e) { toast.error('Failed to update'); }
    finally { setEditSaving(false); }
  };

  const handleEditLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setEditLogoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd);
      setEditForm(p => ({ ...p, logo_url: res.data.url }));
    } catch { toast.error('Failed to upload'); }
    finally { setEditLogoUploading(false); }
  };

  const getTrackingUrl = (c) => `${BASE}/api/q/${c.code}`;
  const getDisplayUrl = (c) => `createonelink.com/${c.code}`;

  const copyUrl = async (c) => {
    try {
      await navigator.clipboard.writeText(getDisplayUrl(c));
      setCopied(c.id);
      toast.success('Link copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd);
      setForm(p => ({ ...p, logo_url: res.data.url }));
      toast.success('Logo uploaded!');
    } catch { toast.error('Failed to upload logo'); }
    finally { setLogoUploading(false); }
  };

  const applyPreset = (preset) => {
    setForm(p => ({ ...p, fg_color: preset.fg, bg_color: preset.bg }));
  };

  const getStyleRadius = (style) => STYLES.find(s => s.id === style)?.radius || '0';

  const downloadQR = (qr) => {
    const container = qrRefs.current[qr.id];
    if (!container) return;
    const svg = container.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    const size = 1024;
    const padding = 80;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const bgColor = qr.bg_color || '#FFFFFF';
    const radius = qr.style === 'circle' ? size / 2 : qr.style === 'rounded' ? 48 : 0;

    // Draw rounded/circle background
    ctx.beginPath();
    if (radius > 0) {
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
    } else {
      ctx.rect(0, 0, size, size);
    }
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.clip();

    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, padding, padding, size - padding * 2, size - padding * 2);

      // Draw logo if present
      if (qr.logo_url) {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.onload = () => {
          const logoSize = size * 0.2;
          const logoX = (size - logoSize) / 2;
          const logoY = (size - logoSize) / 2;
          ctx.fillStyle = bgColor;
          ctx.fillRect(logoX - 4, logoY - 4, logoSize + 8, logoSize + 8);
          ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
          triggerDownload(canvas, qr);
        };
        logo.onerror = () => toast.error('Unable to load the QR logo. Check the image URL or its CORS settings.');
        logo.src = resolveCanvasMediaUrl(qr.logo_url);
      } else {
        triggerDownload(canvas, qr);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const triggerDownload = (canvas, qr) => {
    const link = document.createElement('a');
    link.download = `qr-${qr.title.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  const viewAnalytics = async (id) => {
    if (analyticsId === id) { setAnalyticsId(null); setAnalyticsData(null); return; }
    setAnalyticsId(id);
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/qr-codes/${id}/analytics`);
      setAnalyticsData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const renderQR = (qr, size = 80) => {
    const fg = qr.fg_color || '#000000';
    const bg = qr.bg_color || '#FFFFFF';
    const logoUrl = qr.logo_url;
    const imageSettings = logoUrl ? {
      src: resolveCanvasMediaUrl(logoUrl),
      height: size * 0.22,
      width: size * 0.22,
      excavate: true,
    } : undefined;
    return (
      <QRCodeSVG value={getTrackingUrl(qr)} size={size} level="H" fgColor={fg} bgColor={bg}
        imageSettings={imageSettings} />
    );
  };

  return (
    <div className="max-w-4xl" data-testid="qr-codes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">QR Codes</h1>
          <p className="text-[#A1A1AA] mt-1 text-sm">Generate branded, trackable QR codes for any link</p>
        </div>
        <Button data-testid="create-qr-btn" onClick={() => setShowForm(true)}
          className="bg-violet-600 hover:bg-violet-700 rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Create QR Code
        </Button>
      </div>

      {/* ══════ CREATE FORM ══════ */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Create Branded QR Code</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#71717A]" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Form Fields */}
              <div className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Destination URL *</Label>
                  <Input data-testid="qr-url" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                    className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://example.com/any-link" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Title (optional)</Label>
                  <Input data-testid="qr-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="My Event Poster" />
                </div>

                {/* Color Presets */}
                <div>
                  <Label className="text-[#A1A1AA] text-xs mb-2 block">Color Theme</Label>
                  <div className="flex flex-wrap gap-2" data-testid="color-presets">
                    {PRESETS.map(p => (
                      <button key={p.name} data-testid={`preset-${p.name.toLowerCase()}`}
                        onClick={() => applyPreset(p)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-medium transition-all ${
                          form.fg_color === p.fg && form.bg_color === p.bg
                            ? 'border-violet-500 bg-violet-500/10 text-white'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#525252]'
                        }`}>
                        <span className="w-3 h-3 rounded-sm border border-[#27272A]" style={{ background: p.fg }} />
                        <span className="w-3 h-3 rounded-sm border border-[#27272A]" style={{ background: p.bg }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label className="text-[#A1A1AA] text-xs">QR Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={form.fg_color} data-testid="fg-color-picker"
                        onChange={e => setForm(p => ({ ...p, fg_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border border-[#27272A] bg-transparent" />
                      <Input value={form.fg_color} onChange={e => setForm(p => ({ ...p, fg_color: e.target.value }))}
                        className="bg-[#1E1E1E] border-[#27272A] text-white text-xs h-8 font-mono" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label className="text-[#A1A1AA] text-xs">Background</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={form.bg_color} data-testid="bg-color-picker"
                        onChange={e => setForm(p => ({ ...p, bg_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border border-[#27272A] bg-transparent" />
                      <Input value={form.bg_color} onChange={e => setForm(p => ({ ...p, bg_color: e.target.value }))}
                        className="bg-[#1E1E1E] border-[#27272A] text-white text-xs h-8 font-mono" />
                    </div>
                  </div>
                </div>

                {/* Style */}
                <div>
                  <Label className="text-[#A1A1AA] text-xs mb-2 block">Shape</Label>
                  <div className="flex gap-2" data-testid="style-picker">
                    {STYLES.map(s => (
                      <button key={s.id} data-testid={`style-${s.id}`}
                        onClick={() => setForm(p => ({ ...p, style: s.id }))}
                        className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all ${
                          form.style === s.id
                            ? 'border-violet-500 bg-violet-500/10 text-white'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#525252]'
                        }`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <Label className="text-[#A1A1AA] text-xs mb-2 block">Logo (optional)</Label>
                  <FileDropzone
                    {...IMAGE_UPLOAD}
                    compact
                    testId="qr-logo-dropzone"
                    label={form.logo_url ? 'Replace logo or drag a new image' : 'Choose logo or drag and drop'}
                    disabled={logoUploading}
                    onUploaded={(result) => setForm(p => ({ ...p, logo_url: result.url }))}
                    successMessage="Logo uploaded"
                    className="mb-2"
                  />
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  {form.logo_url ? (
                    <div className="flex items-center gap-3">
                      <img src={resolveMediaUrl(form.logo_url)}
                        alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#27272A]" />
                      <button onClick={() => setForm(p => ({ ...p, logo_url: '' }))}
                        className="text-red-400 text-xs hover:text-red-300">Remove</button>
                    </div>
                  ) : (
                    <button data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()} disabled={logoUploading}
                      className="w-full py-3 rounded-xl border border-dashed border-[#27272A] text-[#71717A] text-xs hover:border-[#525252] hover:text-[#A1A1AA] transition-all flex items-center justify-center gap-2">
                      {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {logoUploading ? 'Uploading...' : 'Upload your logo'}
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Live Preview */}
              <div className="flex flex-col items-center justify-center">
                <p className="text-[#525252] text-[10px] uppercase tracking-widest mb-3">Live Preview</p>
                <div className="p-4 rounded-xl border border-[#27272A]" style={{ backgroundColor: form.bg_color, borderRadius: getStyleRadius(form.style) }}
                  data-testid="qr-preview">
                  <QRCodeSVG
                    value={form.url || 'https://createonelink.com'}
                    size={180}
                    level="H"
                    fgColor={form.fg_color}
                    bgColor={form.bg_color}
                    imageSettings={form.logo_url ? {
                      src: resolveCanvasMediaUrl(form.logo_url),
                      height: 40,
                      width: 40,
                      excavate: true,
                    } : undefined}
                  />
                </div>
                <p className="text-[#525252] text-[10px] mt-3">
                  {form.url ? `createonelink.com/${_extract_prefix(form.title, form.url)}...` : 'Enter a URL to preview'}
                </p>
              </div>
            </div>

            <Button data-testid="save-qr" onClick={handleCreate} disabled={saving}
              className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 h-10 mt-5">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
              Generate QR Code
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════ LIST ══════ */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : codes.length === 0 && !showForm ? (
        <div className="text-center py-20 bg-[#121212] rounded-2xl border border-[#27272A]">
          <QrCode className="w-12 h-12 text-[#525252] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No QR codes yet</h3>
          <p className="text-[#71717A] text-sm mb-4">Create branded, trackable QR codes for any link</p>
          <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700 rounded-xl">
            <Plus className="w-4 h-4 mr-1" /> Create QR Code
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {codes.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}>
              <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4" data-testid={`qr-card-${i}`}>
                {editingId === c.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-medium text-sm">Edit QR Code</p>
                      <button onClick={cancelEdit}><X className="w-4 h-4 text-[#71717A]" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-[#A1A1AA] text-xs">URL</Label>
                          <Input data-testid={`edit-qr-url-${i}`} value={editForm.url} onChange={e => setEditForm(p => ({ ...p, url: e.target.value }))}
                            className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                        </div>
                        <div>
                          <Label className="text-[#A1A1AA] text-xs">Title</Label>
                          <Input data-testid={`edit-qr-title-${i}`} value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                            className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                        </div>
                        <div>
                          <Label className="text-[#A1A1AA] text-xs mb-1.5 block">Color Theme</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {PRESETS.map(p => (
                              <button key={p.name} onClick={() => setEditForm(prev => ({ ...prev, fg_color: p.fg, bg_color: p.bg }))}
                                className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[9px] font-medium ${
                                  editForm.fg_color === p.fg && editForm.bg_color === p.bg ? 'border-violet-500 bg-violet-500/10 text-white' : 'border-[#27272A] text-[#71717A]'}`}>
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.fg }} />
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.bg }} />
                                {p.name}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <Label className="text-[#A1A1AA] text-xs">QR Color</Label>
                            <div className="flex items-center gap-1.5 mt-1">
                              <input type="color" value={editForm.fg_color} onChange={e => setEditForm(p => ({ ...p, fg_color: e.target.value }))}
                                className="w-7 h-7 rounded cursor-pointer border border-[#27272A] bg-transparent" />
                              <Input value={editForm.fg_color} onChange={e => setEditForm(p => ({ ...p, fg_color: e.target.value }))}
                                className="bg-[#1E1E1E] border-[#27272A] text-white text-[10px] h-7 font-mono" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <Label className="text-[#A1A1AA] text-xs">Background</Label>
                            <div className="flex items-center gap-1.5 mt-1">
                              <input type="color" value={editForm.bg_color} onChange={e => setEditForm(p => ({ ...p, bg_color: e.target.value }))}
                                className="w-7 h-7 rounded cursor-pointer border border-[#27272A] bg-transparent" />
                              <Input value={editForm.bg_color} onChange={e => setEditForm(p => ({ ...p, bg_color: e.target.value }))}
                                className="bg-[#1E1E1E] border-[#27272A] text-white text-[10px] h-7 font-mono" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-[#A1A1AA] text-xs mb-1.5 block">Shape</Label>
                          <div className="flex gap-1.5">
                            {STYLES.map(s => (
                              <button key={s.id} onClick={() => setEditForm(p => ({ ...p, style: s.id }))}
                                className={`flex-1 py-1.5 rounded-md border text-[10px] font-medium ${
                                  editForm.style === s.id ? 'border-violet-500 bg-violet-500/10 text-white' : 'border-[#27272A] text-[#71717A]'}`}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-[#A1A1AA] text-xs mb-1.5 block">Logo</Label>
                          <FileDropzone
                            {...IMAGE_UPLOAD}
                            compact
                            testId={`qr-edit-logo-dropzone-${c.id}`}
                            label="Choose logo or drag and drop"
                            disabled={editLogoUploading}
                            onUploaded={(result) => setEditForm(p => ({ ...p, logo_url: result.url }))}
                            successMessage="Logo uploaded"
                            className="mb-2"
                          />
                          <input ref={editLogoRef} type="file" accept="image/*" className="hidden" onChange={handleEditLogoUpload} />
                          {editForm.logo_url ? (
                            <div className="flex items-center gap-2">
                              <img src={resolveMediaUrl(editForm.logo_url)}
                                alt="" className="w-8 h-8 rounded object-cover border border-[#27272A]" />
                              <button onClick={() => editLogoRef.current?.click()} className="text-violet-400 text-[10px]">Change</button>
                              <button onClick={() => setEditForm(p => ({ ...p, logo_url: '' }))} className="text-red-400 text-[10px]">Remove</button>
                            </div>
                          ) : (
                            <button onClick={() => editLogoRef.current?.click()} disabled={editLogoUploading}
                              className="w-full py-2 rounded-lg border border-dashed border-[#27272A] text-[#71717A] text-[10px] hover:border-[#525252] flex items-center justify-center gap-1.5">
                              {editLogoUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                              {editLogoUploading ? 'Uploading...' : 'Upload logo'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <p className="text-[#525252] text-[9px] uppercase tracking-widest mb-2">Preview</p>
                        <div className="p-3 rounded-xl border border-[#27272A]"
                          style={{ backgroundColor: editForm.bg_color, borderRadius: getStyleRadius(editForm.style) }}>
                          <QRCodeSVG value={editForm.url || 'https://createonelink.com'} size={120} level="H"
                            fgColor={editForm.fg_color} bgColor={editForm.bg_color}
                            imageSettings={editForm.logo_url ? { src: resolveCanvasMediaUrl(editForm.logo_url), height: 26, width: 26, excavate: true } : undefined} />
                        </div>
                        <p className="text-[#525252] text-[9px] mt-2">{getDisplayUrl(c)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={cancelEdit} className="flex-1 py-2 rounded-lg border border-[#27272A] text-[#A1A1AA] text-xs hover:bg-white/5">Cancel</button>
                      <Button data-testid={`save-qr-edit-${i}`} onClick={handleUpdate} disabled={editSaving}
                        className="flex-1 rounded-lg bg-violet-600 hover:bg-violet-700 h-9 text-xs">
                        {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                <div className="flex gap-4">
                  <div ref={el => qrRefs.current[c.id] = el}
                    className="w-24 h-24 flex-shrink-0 flex items-center justify-center p-2"
                    style={{
                      backgroundColor: c.bg_color || '#FFFFFF',
                      borderRadius: getStyleRadius(c.style || 'square'),
                    }}>
                    {renderQR(c, 80)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm truncate">{c.title}</h3>
                    <p className="text-violet-400 text-[10px] truncate mt-0.5">{getDisplayUrl(c)}</p>
                    <p className="text-[#525252] text-[10px] truncate mt-0.5">{c.url}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="flex items-center gap-1 text-xs text-violet-400">
                        <ScanLine className="w-3 h-3" /> {c.scans} scans
                      </span>
                      {c.logo_url && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <Palette className="w-3 h-3" /> Branded
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <button onClick={() => downloadQR(c)} className="p-1.5 rounded-lg hover:bg-white/5" title="Download PNG" data-testid={`download-qr-${i}`}>
                        <Download className="w-4 h-4 text-[#A1A1AA]" />
                      </button>
                      <button onClick={() => copyUrl(c)} className="p-1.5 rounded-lg hover:bg-white/5" title="Copy link">
                        {copied === c.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-[#A1A1AA]" />}
                      </button>
                      <button data-testid={`edit-qr-btn-${i}`} onClick={() => startEdit(c)} className="p-1.5 rounded-lg hover:bg-white/5" title="Edit">
                        <Edit2 className="w-4 h-4 text-[#A1A1AA]" />
                      </button>
                      <button data-testid={`qr-analytics-btn-${i}`} onClick={() => viewAnalytics(c.id)} className="p-1.5 rounded-lg hover:bg-white/5" title="Analytics">
                        <BarChart3 className={`w-4 h-4 ${analyticsId === c.id ? 'text-violet-400' : 'text-[#A1A1AA]'}`} />
                      </button>
                      <a href={c.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-white/5">
                        <ExternalLink className="w-4 h-4 text-[#A1A1AA]" />
                      </a>
                      <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Delete">
                        <Trash2 className="w-4 h-4 text-red-400/60" />
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>
              {/* Analytics Panel */}
              {analyticsId === c.id && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  className="bg-[#0A0A0A] border border-[#27272A] border-t-0 rounded-b-xl p-4 -mt-1" data-testid={`qr-analytics-panel-${i}`}>
                  {analyticsLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-violet-400 animate-spin" /></div>
                  ) : analyticsData ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#121212] rounded-lg p-3 text-center">
                          <ScanLine className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                          <p className="text-white font-bold text-lg">{analyticsData.total_scans}</p>
                          <p className="text-[#71717A] text-[10px]">Total Scans</p>
                        </div>
                        <div className="bg-[#121212] rounded-lg p-3 text-center">
                          <Calendar className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                          <p className="text-white font-bold text-lg">
                            {analyticsData.daily.length > 0 ? analyticsData.daily[analyticsData.daily.length - 1].scans : 0}
                          </p>
                          <p className="text-[#71717A] text-[10px]">Today</p>
                        </div>
                      </div>
                      {analyticsData.daily.length > 0 && (
                        <div>
                          <p className="text-[#A1A1AA] text-xs mb-2">Scan History</p>
                          <div className="flex items-end gap-1 h-16">
                            {analyticsData.daily.slice(-14).map((d, idx) => {
                              const max = Math.max(...analyticsData.daily.slice(-14).map(x => x.scans));
                              const h = max > 0 ? (d.scans / max) * 100 : 0;
                              return (
                                <div key={idx} className="flex-1 group relative">
                                  <div className="bg-violet-500/60 rounded-t-sm transition-all group-hover:bg-violet-400"
                                    style={{ height: `${Math.max(h, 4)}%` }} />
                                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#1E1E1E] px-1.5 py-0.5 rounded text-[9px] text-white opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                                    {d.scans} • {d.date.slice(5)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {analyticsData.recent_scans.length > 0 && (
                        <div>
                          <p className="text-[#A1A1AA] text-xs mb-2">Recent Scans</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {analyticsData.recent_scans.slice(0, 10).map((s, idx) => (
                              <div key={idx} className="text-[10px] text-[#71717A]">
                                {new Date(s.scanned_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Extract prefix for preview display - mirrors backend logic */
function _extract_prefix(title, url) {
  let source = (title || '').trim();
  if (!source) {
    try { source = url.split('//')[1]?.split('/')[0]?.replace('www.', '').split('.')[0] || 'lnk'; }
    catch { source = 'lnk'; }
  }
  const letters = source.replace(/[^a-zA-Z]/g, '').toLowerCase();
  return letters.length >= 3 ? letters.slice(0, 3) : letters.padEnd(3, 'x');
}

export default QRCodes;
